import { useState } from 'react';
import './FAQs.css';

function FAQs() {
  const [tapOpen, setTapOpen] = useState(false);
  const [edictOpen, setEdictOpen] = useState(false);

  const tapFaqs = [
    {
      question: "What is TAP?",
      answer: "TAP is a community-building platform that helps you meet new people through casual, no-pressure hangouts at public places. Create or join TAPs for brief, friendly conversations while you're already out doing what you love."
    },
    {
      question: "How does TAP work?",
      answer: "Simply create a TAP when you're at a coffee shop, park, bookstore, or any public space where you'll be for a while. Others can join your TAP for casual conversation. No commitments, no expectations - just genuine human connection."
    },
    {
      question: "What are TAP Deals?",
      answer: "TAP Deals are exclusive offers from local businesses like coffee shops and bars. For example, you might find deals like 'free coffee with pastry purchase' that you can redeem while meeting new people."
    },
    {
      question: "How long do TAP conversations typically last?",
      answer: "TAP conversations usually last 5-15 minutes. They're designed to be those natural, spontaneous chats you might have with someone while you're both enjoying the same space."
    },
    {
      question: "Is TAP a dating app?",
      answer: "No, TAP is not a dating app. We're focused on casual, friendly conversations and community building. Think of it as a way to meet neighbors and locals, not romantic connections."
    },
    {
      question: "How do I become a TAP vendor partner?",
      answer: "Local businesses can become TAP partners by contacting us at support@smallrobot.app with 'Vendor Partnership' in the subject line. You'll be able to create TAP Deals and track foot traffic through our vendor portal."
    },
    {
      question: "What kind of businesses work well with TAP?",
      answer: "Coffee shops, bars, bookstores, parks with cafes, co-working spaces, and other community-focused businesses are perfect for TAP. Any place where people naturally gather and feel comfortable having conversations."
    },
    {
      question: "Is TAP safe?",
      answer: "Yes, TAP emphasizes safety through public meeting places, brief interaction times, and community guidelines. All hangouts happen in public spaces, and there's no pressure for extended meetings."
    }
  ];

  const edictFaqs = [
    {
      question: "What is EDICT?",
      answer: "EDICT is a social platform where you take control of a parallel dimension. Issue edicts — rules and commands — and watch your world evolve through ground-breaking journalism, impact photos, interdimensional ads, and more."
    },
    {
      question: "How does EDICT work?",
      answer: "Once you enter your dimension, you issue edicts — bold declarations that shape your world. The effects of your edicts play out through news coverage, photography, and other media that reflects the state of your reality."
    },
    {
      question: "What is an edict?",
      answer: "An edict is a command or ruling you issue as the ruler of your parallel dimension. Edicts can be sweeping or subtle, and their consequences unfold in unexpected and entertaining ways."
    },
    {
      question: "What is interdimensional journalism?",
      answer: "Interdimensional journalism is EDICT's unique take on news — reporters from your dimension cover the fallout of your edicts in real time, bringing your world to life through headlines, photos, and breaking stories."
    },
    {
      question: "What are interdimensional ads?",
      answer: "Interdimensional ads are fictional advertisements that exist within your dimension, shaped by the edicts you've issued. They add personality and depth to your parallel world."
    }
  ];

  return (
    <div className="faqs">
      <section className="faqs-hero">
        <div className="faqs-hero-img">
          <img src="/assets/hero_img.png" alt="Hero Image" />
        </div>
        <div className="container">
          <h1>Questions & Answers</h1>
          <p className="hero-subtitle">Everything you need to know about TAP and Small Robot</p>
        </div>
      </section>

      <section className="faqs-content">
        <div className="container">

          <div className="faq-section">
            <button
              className={`faq-section-toggle ${tapOpen ? 'open' : ''}`}
              onClick={() => setTapOpen(!tapOpen)}
            >
              <span>TAP</span>
              <span className="faq-toggle-icon">{tapOpen ? '−' : '+'}</span>
            </button>
            {tapOpen && (
              <div className="faqs-list">
                {tapFaqs.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <h3 className="faq-question">{faq.question}</h3>
                    <p className="faq-answer">{faq.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="faq-section">
            <button
              className={`faq-section-toggle ${edictOpen ? 'open' : ''}`}
              onClick={() => setEdictOpen(!edictOpen)}
            >
              <span>EDICT!</span>
              <span className="faq-toggle-icon">{edictOpen ? '−' : '+'}</span>
            </button>
            {edictOpen && (
              <div className="faqs-list">
                {edictFaqs.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <h3 className="faq-question">{faq.question}</h3>
                    <p className="faq-answer">{faq.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="contact-cta">
            <h3>Still have questions?</h3>
            <p>Can't find what you're looking for? Get in touch with our team.</p>
            <a href="mailto:support@smallrobot.app" className="contact-button">
              Contact Support
            </a>
          </div>

          <div className="deletion-cta">
            <h3>Request Account Deletion</h3>
            <p>
              You can delete your Small Robot account and associated data at any time using either method below. Please let us know which app you'd like to be removed from.
            </p>
            <ol className="deletion-steps">
              <li><strong>In-app:</strong> Open the app → Settings → Account → Delete Account</li>
              <li><strong>By email:</strong> Send a deletion request from the email address linked to your account to <a href="mailto:support@smallrobot.app">support@smallrobot.app</a> with the subject line "Account Deletion Request". Please include which app (TAP or EDICT) you'd like to be removed from. We will process your request within 30 days.</li>
            </ol>
            <p>
              When your account is deleted, the following data is permanently removed: your profile, content, history, and all associated personal data.
            </p>
            <p className="deletion-note">
              Note: certain data — such as fraud-prevention logs and security records — may be retained for up to 30 days where required for security, fraud prevention, or legal compliance. See our <a href="/privacy">Privacy Policy</a> for details.
            </p>
            <a
              href="mailto:support@smallrobot.app?subject=Account%20Deletion%20Request&body=Please%20delete%20my%20account%20and%20all%20associated%20data.%0A%0AApp%20(TAP%20or%20EDICT)%3A%20%0AEmail%20address%20on%20account%3A%20"
              className="deletion-button"
            >
              Submit Deletion Request
            </a>
          </div>

        </div>
      </section>
    </div>
  );
}

export default FAQs;
